import {
  getAllowEditMap,
  ITEM_STATUS
} from "@saleor/createOrChangeNotRefContract/constants";
import { ItemTab } from "@saleor/createOrChangeNotRefContract/utils";
import { useEffect, useMemo, useState } from "react";

import useStyles from "./styles";
import useFormatters from "./useFormatters";

const useItemDetailsData = ({
  formik,
  isChangeFlow,
  isViewMode,
  handleSplit,
  deleteChangeOrderLines,
  formikHeader,
  paymentTermList,
  undoCancelOrderLine,
  currentAction,
  disabledActionForCashSale
}) => {
  const [searchModal, setSearchModal] = useState<{
    isOpen: boolean;
    id: string;
    lineId: string;
  }>({ isOpen: false, id: null, lineId: null });
  const handleOpenSearchModal = (id, lineId) => {
    setSearchModal({ isOpen: true, id, lineId });
  };

  const handleCloseSearchModal = () => {
    setSearchModal({ isOpen: false, id: null, lineId: null });
  };

  const [shipToModal, setShipToModal] = useState<{
    isOpen: boolean;
    index: number;
    shiToId: string;
  }>({ isOpen: false, index: -1, shiToId: null });

  const handleOpenShipToModal = (index, shiToId) => {
    setShipToModal({ isOpen: true, index, shiToId });
  };
  const orderTypeS = formikHeader?.values?.orderType === "ZP20";
  const handleCloseShipToModal = () => {
    setShipToModal({ isOpen: false, index: -1, shiToId: null });
  };

  const [saleTextModal, setSaleTextModal] = useState<{
    isOpen: boolean;
    saleTextValue: string;
    title: string;
    key: string;
    index: number;
  }>({ isOpen: false, saleTextValue: "", title: "", key: "", index: -1 });

  const handleOpenSaleTextModal = (saleTextValue, title, index, key) => {
    setSaleTextModal({ isOpen: true, saleTextValue, title, index, key });
  };

  const handleCloseSaleTextModal = () => {
    setSaleTextModal({
      isOpen: false,
      saleTextValue: "",
      title: "",
      index: -1,
      key: ""
    });
  };

  const classes = useStyles();
  const [activeTab, setActiveTab] = useState<ItemTab>(ItemTab.tabA);
  const [columnsList, setColumnsList] = useState([]);
  const {
    quantityFormatter,
    quantityUnitFormatter,
    productionFlagFormatter,
    batchNumberFormatter,
    usePlantSearchFormatter,
    poDetailsFormatter,
    poItemNumberFormatter,
    changeOrderOsFormatter,
    changeOrderOsHeaderFormatter,
    changeOrderItemOsHeaderFormatter,
    pricePerUnitFormatter,
    priceDateFormatter,
    requestDateFormatter,
    originalDateFormatter,
    unitOfMeasureFormatter,
    unitOfMeasureHeaderFormatter,
    deliveryBlockedFormatter,
    shippingPointFormatter,
    ItemNoFormatter,
    materialDescriptionFormatter,
    textBoxFormatter,
    shipToPartyFormatter,
    lotNoPpFormatter,
    CIPPPFormatter,
    materialCodeFormatter,
    emptyMiddleware,
    CommonDecimalFormatter,
    deliveryToleranceHeaderFormatter,
    deliveryToleranceFormatter,
    netPriceFormatter,
    plantApplyAllHeaderFormatter,
    requestDateApplyAllHeaderFormatter,
    priceDateApplyAllHeaderFormatter,
    paymentTermFormatter,
    requiredHeaderFormatter,
    itemStatusFormatter,
    itemCatFormater,
    saleTextFormatter
  } = useFormatters({
    handleOpenSearchModal,
    handleOpenShipToModal,
    formik,
    formikHeader,
    handleSplit,
    deleteChangeOrderLines,
    isChangeFlow,
    undoCancelOrderLine,
    orderTypeS,
    disabledActionForCashSale,
    handleOpenSaleTextModal
  });

  const onTabChange = tab => {
    setActiveTab(tab);
  };

  const rowsData = formik.values.lines;

  const tabList: TableTab[] = [
    {
      id: "order-information-tab",
      code: ItemTab.tabA,
      title: "ข้อมูลสั่งซื้อ",
      index: "z3",
      first: true
    },
    {
      id: "additional-tab",
      code: ItemTab.tabC,
      title: "Additional Data",
      index: "z2",
      first: false
    },
    {
      id: "cash-sale-tab",
      code: ItemTab.tabD,
      title: "Cash Sale",
      index: "z1",
      first: false
    }
  ];

  function hideAllSelect() {
    return isChangeFlow && (isViewMode || currentAction === "add");
  }

  const commonColumns = useMemo<TableColumn[]>(
    () => [
      {
        id: "itemNo",
        label: "Item No.",
        className: classes.freezCell,
        style: {
          minWidth: 56,
          fontSize: 12,
          textAlign: "center",
          left: hideAllSelect() ? 0 : 49,
          padding: 4,
          fontWeight: "bold"
        },
        valueRerender: row => row?.priceStatus,
        formatter: ItemNoFormatter
      },
      {
        id: "materialCode",
        label: "Material Code",
        style: {
          minWidth: hideAllSelect() ? 200 : 240,
          left: hideAllSelect() ? 56 : 105,
          fontWeight: "bold"
        },
        className: classes.freezCell,
        formatter: (row, col, index, mode) =>
          materialCodeFormatter(row, col, index, mode, isChangeFlow),
        align: "center"
      },
      {
        id: "materialDescription",
        label: "Material Description",
        style: {
          minWidth: 284,
          left: hideAllSelect() ? 256 : 345,
          fontWeight: "bold"
        },
        className: classes.freezCell,
        align: "left",
        formatter: materialDescriptionFormatter,
        valueRerender: row => row?.isSplitEnabled
      }
    ],
    [isViewMode, currentAction]
  );

  const OrderInformationColumnsList = useMemo<TableColumn[]>(() => {
    const columns: TableColumn[] = [
      {
        id: "customerMatCode",
        label: "Customer Material",
        style: { minWidth: 152 }
      },
      {
        id: "quantity",
        label: "จำนวน",
        style: { minWidth: 96 },
        headerFormatter: col => requiredHeaderFormatter(col),
        formatter: quantityFormatter,
        align: "right",
        child: {
          align: "left",
          id: "salesUnit",
          label: "หน่วย",
          style: { minWidth: 88 },
          formatter: quantityUnitFormatter,
          allowEdit: getAllowEditMap(orderTypeS, "salesUnit")
        },
        allowEdit: getAllowEditMap(orderTypeS, "quantity")
      },
      {
        id: "weight",
        label: "น้ำหนัก",
        style: { minWidth: 64 },
        align: "right",
        formatter: (row, cell) => CommonDecimalFormatter(row?.[cell?.id]),
        child: {
          align: "left",
          id: "weightUnit",
          label: "หน่วย",
          style: { minWidth: 64 }
        }
      },
      {
        id: "batchNo",
        label: "Batch No.",
        style: { minWidth: 250 },
        formatter: batchNumberFormatter,
        allowEdit: getAllowEditMap(orderTypeS, "batchNo")
      },
      {
        id: "plant",
        label: "Plant",
        style: { minWidth: 128 },
        formatter: usePlantSearchFormatter,
        headerFormatter: (col, index, mode) =>
          plantApplyAllHeaderFormatter(col, index, mode, orderTypeS),
        allowEdit: getAllowEditMap(orderTypeS, "plant")
      },
      {
        id: "requestDate",
        label: "Request Date",
        style: { minWidth: 200 },
        formatter: requestDateFormatter,
        headerFormatter: (col, index, mode) =>
          requestDateApplyAllHeaderFormatter(col, index, mode, orderTypeS),
        allowEdit: getAllowEditMap(orderTypeS, "requestDate"),
        valueRerender: row =>
          row?.itemStatusEn === ITEM_STATUS.COMPLETE_DELIVERY &&
          row?.quantity <= row?.originalQuantity
      },
      {
        id: "originalRequestDate",
        label: "Original Date",
        style: { minWidth: 112 },
        formatter: originalDateFormatter
      },
      {
        id: "pricePerUnit",
        label: "ราคาต่อหน่วย",
        style: { minWidth: 112 },
        formatter: pricePerUnitFormatter,
        valueRerender: row => row?.manualPriceFlag || row?.priceStatus,
        allowEdit: getAllowEditMap(orderTypeS, "pricePerUnit")
      },
      {
        id: "netValue",
        label: "ราคารวมสุทธิ",
        style: { minWidth: 104 },
        align: "right",
        formatter: netPriceFormatter,
        valueRerender: row => row?.manualPriceFlag || row?.priceStatus,
        child: {
          align: "left",
          id: "priceCurrency",
          label: "Currency",
          style: { minWidth: 64 },
          formatter: row => (!row.parentItemNo ? row?.priceCurrency : null)
        }
      },
      {
        id: "productionFlag",
        label: "Production Flag",
        style: { minWidth: 104 },
        formatter: productionFlagFormatter,
        allowEdit: getAllowEditMap(orderTypeS, "productionFlag")
      },
      {
        id: "deliveryTolerance",
        label: "Delivery Tolerance",
        style: { minWidth: 160 },
        align: "center",
        headerStyle: { background: "#eee", minWidth: 160 },
        headerFormatter: deliveryToleranceHeaderFormatter,
        formatter: deliveryToleranceFormatter,
        allowEdit: getAllowEditMap(orderTypeS, "deliveryTolerance")
      },
      {
        id: "poDetails",
        label: "PO Detail",
        style: { minWidth: 128 },
        formatter: poDetailsFormatter,
        allowEdit: getAllowEditMap(orderTypeS, "poDetails")
      },
      {
        id: "paymentTermItem",
        label: "เงื่อนไขการชำระเงิน",
        style: { minWidth: 96 },
        formatter: (row, col, index, mode) =>
          paymentTermFormatter(row, col, index, mode, paymentTermList),
        allowEdit: getAllowEditMap(orderTypeS, "paymentTermItem")
      },
      {
        id: "priceDate",
        label: "Price Date",
        style: { minWidth: 190 },
        formatter: priceDateFormatter,
        headerFormatter: (col, index, mode) =>
          priceDateApplyAllHeaderFormatter(col, index, mode, orderTypeS),
        allowEdit: getAllowEditMap(orderTypeS, "priceDate")
      },
      {
        id: "itemCategory",
        label: "Item Cat.",
        style: { minWidth: 88 },
        formatter: itemCatFormater
      },

      {
        id: "poItemNumber",
        label: "PO Item Number",
        style: { minWidth: 72 },
        formatter: poItemNumberFormatter,
        allowEdit: getAllowEditMap(orderTypeS, "poItemNumber")
      },
      {
        id: "prNoOs",
        label: "PR Number (OS)",
        style: { minWidth: 96 },
        formatter: changeOrderOsFormatter,
        headerFormatter: () => changeOrderOsHeaderFormatter("PR Number")
      },
      {
        id: "prItemNoOs",
        label: "PR Item No. (OS)",
        style: { minWidth: 72 },
        formatter: changeOrderOsFormatter,
        headerFormatter: () => changeOrderItemOsHeaderFormatter("PR Item No. ")
      },
      {
        id: "poNoOs",
        label: "PO Number (OS)",
        style: { minWidth: 96 },
        formatter: changeOrderOsFormatter,
        headerFormatter: () => changeOrderOsHeaderFormatter("PO Number")
      },
      {
        id: "poItemNoOs",
        label: "PO Item No. (OS)",
        style: { minWidth: 72 },
        formatter: changeOrderOsFormatter,
        headerFormatter: () => changeOrderItemOsHeaderFormatter("PO Item No. ")
      },
      {
        id: "poGrStatusOs",
        label: "PO/GR Status (OS)",
        style: { minWidth: 112 },
        formatter: changeOrderOsFormatter,
        headerFormatter: () => changeOrderOsHeaderFormatter("PO/GR Status")
      },
      {
        id: "itemStatusEn",
        label: "Item Status",
        style: { minWidth: 128 },
        formatter: itemStatusFormatter
      },
      {
        id: "saleQtyFactor",
        label: "Unit of measure",
        style: { minWidth: 174 },
        formatter: unitOfMeasureFormatter,
        headerFormatter: mode =>
          unitOfMeasureHeaderFormatter(mode, isChangeFlow, currentAction),
        allowEdit: getAllowEditMap(orderTypeS, "saleQtyFactor")
      },
      {
        id: "deliveryBlock",
        label: "Delivery Blocked",
        style: { minWidth: 88 },
        formatter: deliveryBlockedFormatter
      },
      {
        id: "shippingPoint",
        label: "Shipping Point",
        style: { minWidth: 88 },
        formatter: shippingPointFormatter,
        allowEdit: getAllowEditMap(orderTypeS, "shippingPoint")
      }
    ];

    const filteredColumns = !isChangeFlow
      ? columns.filter(
          column =>
            ![
              "itemCategory",
              "originalRequestDate",
              "prNoOs",
              "prItemNoOs",
              "poNoOs",
              "poItemNoOs",
              "poGrStatusOs",
              "itemStatusEn",
              "saleQtyFactor",
              "deliveryBlock",
              "shippingPoint"
            ].includes(column.id)
        )
      : columns;

    return filteredColumns;
  }, []);

  const additionalDataColumnList = useMemo<TableColumn[]>(
    () => [
      {
        id: "shipTo",
        label: "ShipTo Party",
        style: { minWidth: 312 },
        formatter: shipToPartyFormatter,
        allowEdit: getAllowEditMap(orderTypeS, "shipTo"),
        valueRerender: row =>
          row?.otcShipTo &&
          Object.keys(row?.otcShipTo)?.length &&
          JSON.stringify(row?.otcShipTo)
      },
      {
        id: "internalCommentsToWarehouse",
        label: "Internal Comments to Warehouse",
        style: { minWidth: 248 },
        formatter: textBoxFormatter(),
        allowEdit: getAllowEditMap(orderTypeS, "internalCommentsToWarehouse")
      },
      {
        id: "externalCommentsToCustomer",
        label: "External Comments to Customer",
        style: { minWidth: 248 },
        formatter: textBoxFormatter(),
        allowEdit: getAllowEditMap(orderTypeS, "externalCommentsToCustomer")
      },
      {
        id: "saleText_1",
        label: "Sale Text 1",
        style: { minWidth: 248 },
        formatter: saleTextFormatter("Sale Text 1"),
        allowEdit: getAllowEditMap(orderTypeS, "saleText_1")
      },
      {
        id: "saleText_2",
        label: "Sale Text 2",
        style: { minWidth: 248 },
        formatter: saleTextFormatter("Sale Text 2"),
        allowEdit: getAllowEditMap(orderTypeS, "saleText_2")
      },
      {
        id: "saleText_3",
        label: "Sale Text 3",
        style: { minWidth: 248 },
        formatter: saleTextFormatter("Sale Text 3"),
        allowEdit: getAllowEditMap(orderTypeS, "saleText_3")
      },
      {
        id: "saleText_4",
        label: "Sale Text 4",
        style: { minWidth: 248 },
        formatter: saleTextFormatter("Sale Text 4"),
        allowEdit: getAllowEditMap(orderTypeS, "saleText_4")
      },
      {
        id: "remark",
        label: "Remark",
        style: { minWidth: 248 },
        formatter: textBoxFormatter(),
        allowEdit: getAllowEditMap(orderTypeS, "remark")
      },
      {
        id: "itemNoteCip",
        label: "Item Note",
        style: { minWidth: 248 },
        formatter: textBoxFormatter(),
        headerFormatter: col => CIPPPFormatter(col, "CIP"),
        allowEdit: getAllowEditMap(orderTypeS, "itemNoteCip")
      },
      {
        id: "prItemTextCip",
        label: "PR Item Text",
        style: { minWidth: 248 },
        formatter: textBoxFormatter(),
        headerFormatter: col => CIPPPFormatter(col, "CIP"),
        allowEdit: getAllowEditMap(orderTypeS, "prItemTextCip")
      },
      {
        id: "lotNoPp",
        label: "Lot No.",
        style: { minWidth: 128 },
        formatter: lotNoPpFormatter,
        headerFormatter: col => CIPPPFormatter(col, "PP"),
        allowEdit: getAllowEditMap(orderTypeS, "lotNoPp")
      },
      {
        id: "productionMemoPp",
        label: "Production Memo",
        style: { minWidth: 248 },
        formatter: textBoxFormatter(),
        headerFormatter: col => CIPPPFormatter(col, "PP"),
        allowEdit: getAllowEditMap(orderTypeS, "productionMemoPp")
      }
    ],
    []
  );
  useEffect(() => {
    if (activeTab === ItemTab.tabA) {
      setColumnsList([...commonColumns, ...OrderInformationColumnsList]);
    }
    if (activeTab === ItemTab.tabC) {
      setColumnsList([...commonColumns, ...additionalDataColumnList]);
    }
  }, [activeTab, isViewMode]);

  return useMemo(
    () => ({
      tabList,
      columnsList,
      rowsData,
      activeTab,
      onTabChange,
      searchModal,
      handleCloseSearchModal,
      emptyMiddleware,
      shipToModal,
      handleCloseShipToModal,
      handleOpenSearchModal,
      saleTextModal,
      handleCloseSaleTextModal
    }),

    [
      rowsData,
      searchModal.id,
      columnsList,
      shipToModal.index,
      handleOpenSearchModal,
      saleTextModal.saleTextValue
    ]
  );
};
export default useItemDetailsData;
